import _isEmpty from 'lodash/isEmpty';

import {
    clearFilterSeenData,
    getAllSeenFilters,
    getStorageStats,
    hasSeenFilter,
    markFilterAsSeen,
} from './filterStorageManager';

/**
 * Utility to determine filter hierarchy and filter data
 * Priority order:
 * 1) Strong Attribute filter
 * 2) Truck/SUV Bodystyle substyle (Truck cab size filter, SUV 3rd row filter)
 * 3) Vehicle Features
 * 4) Suggested filter
 *
 * When all filters in the hierarchy have been seen, the session storage
 * is automatically reset and the cycle starts over from the beginning.
 */

// Constants
const VEHICLE_FEATURE_CODES = [
    '1211', // Bluetooth, Hands-Free
    '1224', // Backup Camera
    '1013', // Security System
    '1313', // Adaptive Cruise Control
    '1136', // Premium Audio
    '1330', // Cross Traffic Alert
    '1310', // Lane Departure Warning
    '1229', // Keyless Start
    '1221', // Remote Engine Start
    '1331', // WiFi Hotspot
];

const FILTER_TYPES = {
    STRONG_ATTRIBUTE: 'strongAttribute',
    SUGGESTED: 'suggested',
    TRUCK: 'truck',
    SUV: 'suv',
    VEHICLE_FEATURE: 'vehicleFeature',
};

// Re-export storage functions for backward compatibility
export {
    hasSeenFilter,
    markFilterAsSeen,
    clearFilterSeenData,
    getAllSeenFilters,
    getStorageStats,
};

// Export constants
export { VEHICLE_FEATURE_CODES };

/**
 * Generate session key for filter tracking
 */
export const getFilterSessionKey = (filterData) => {
    const {
        isStrongAttribute,
        firstStrongAttribute,
        isTruckCard,
        isSUVCard,
        isVehicleFeatureCard,
        randomVehicleFeature,
        isSuggestedFilter,
        firstSuggestedFilter,
    } = filterData;

    if (isStrongAttribute && firstStrongAttribute) {
        const codes = Object.values(firstStrongAttribute.codes || {}).flat();
        return codes.join(',');
    }

    if (isTruckCard) return FILTER_TYPES.TRUCK;
    if (isSUVCard) return FILTER_TYPES.SUV;

    if (isVehicleFeatureCard && randomVehicleFeature) {
        return `${FILTER_TYPES.VEHICLE_FEATURE}:${randomVehicleFeature.value}`;
    }

    if (isSuggestedFilter && firstSuggestedFilter) {
        const codes = Object.values(firstSuggestedFilter.codes || {}).flat();
        return codes.join(',');
    }

    return null;
};

/**
 * Check if a filter is already active/selected
 * Handles both regular filters (with codes) and vehicle features (with value/filterName)
 */
export const isFilterAlreadyActive = (filter, activeFilterNames, activeFeatureValues = []) => {
    // Handle regular filters with codes property
    if (filter?.codes) {
        const codeKeys = Object.keys(filter.codes);
        return codeKeys.some((codeKey) => activeFilterNames.includes(codeKey));
    }

    // Handle vehicle features with value property
    if (filter?.value) {
        // Check if feature value is in active feature values
        const isActiveByValue = activeFeatureValues.includes(filter.value);

        if (isActiveByValue) {
            return true;
        }

        // Check if feature filterName is in active filter names
        if (filter.filterName && activeFilterNames.includes(filter.filterName)) {
            return true;
        }
    }

    return false;
};

/**
 * Find the first inactive and unseen filter
 */
export const findFirstInactiveUnseenFilter = (filters, activeFilterNames, filterType, activeFeatureValues = []) => {
    if (!filters?.length) return null;

    const result = filters.find((filter) => {
        if (isFilterAlreadyActive(filter, activeFilterNames, activeFeatureValues)) return false;

        // Create candidate for session key generation
        const candidate = filterType === FILTER_TYPES.STRONG_ATTRIBUTE
            ? { isStrongAttribute: true, firstStrongAttribute: filter }
            : { isSuggestedFilter: true, firstSuggestedFilter: filter };

        const sessionKey = getFilterSessionKey(candidate);
        return !hasSeenFilter(sessionKey);
    });

    return result || null;
};

/**
 * Get available vehicle features with counts (excluding already selected features)
 */
export const getAvailableVehicleFeatures = (featureFilterOptions) => {
    if (!featureFilterOptions?.options) return [];

    const availableFeatures = featureFilterOptions.options.filter(
        (option) => VEHICLE_FEATURE_CODES.includes(option.value)
            && option.count > 0
            && !option.selected // Exclude already selected features
    );

    return availableFeatures;
};

/**
 * Get the first unseen vehicle feature that is not already active
 */
export const getFirstUnseenVehicleFeature = (availableFeatures, activeFeatureValues = [], activeFilterNames = []) => {
    if (!availableFeatures?.length) return null;

    const result = availableFeatures.find((feature) => {
        // Use the enhanced isFilterAlreadyActive function for consistent checking
        if (isFilterAlreadyActive(feature, activeFilterNames, activeFeatureValues)) {
            return false;
        }

        const sessionKey = getFilterSessionKey({
            isVehicleFeatureCard: true,
            randomVehicleFeature: feature,
        });

        const isSeen = hasSeenFilter(sessionKey);
        return !isSeen;
    });

    return result || null;
};

/**
 * Check if all filters in the hierarchy have been seen
 * Returns true if all available filters across all categories have been seen
 */
export const areAllFiltersInHierarchySeen = (params) => {
    const {
        strongAttributeFilters = [],
        suggestedFilters = [],
        isTruckSelected = false,
        isSUVSelected = false,
        featureFilterOptions = null,
        featureFilterValues = [],
        cabSizeOptions = [],
        cabSizeValues = [],
        activeFilterNames = [],
    } = params;

    // Check Strong Attribute filters
    const hasUnseenStrongAttribute = strongAttributeFilters.some((filter) => {
        if (isFilterAlreadyActive(filter, activeFilterNames)) return false;
        const sessionKey = getFilterSessionKey({
            isStrongAttribute: true,
            firstStrongAttribute: filter,
        });
        return !hasSeenFilter(sessionKey);
    });

    // Check Truck/SUV filters
    const thirdRowFilterOptions = featureFilterOptions?.options?.find(
        (option) => option.value === '1306'
    );
    const thirdRowApplied = featureFilterValues.includes('1306');
    const cabSizeApplied = cabSizeValues.length > 0;

    const shouldShowTruckCard = isTruckSelected && !cabSizeApplied && !_isEmpty(cabSizeOptions);
    const hasUnseenTruckFilter = shouldShowTruckCard && !hasSeenFilter(FILTER_TYPES.TRUCK);

    const shouldShowSUVCard = isSUVSelected && !thirdRowApplied && !_isEmpty(thirdRowFilterOptions);
    const hasUnseenSUVFilter = shouldShowSUVCard && !hasSeenFilter(FILTER_TYPES.SUV);

    // Check Vehicle Feature filters
    const availableVehicleFeatures = getAvailableVehicleFeatures(featureFilterOptions);
    const hasUnseenVehicleFeature = availableVehicleFeatures.some((feature) => {
        // Use the enhanced isFilterAlreadyActive function for consistent checking
        if (isFilterAlreadyActive(feature, activeFilterNames, featureFilterValues)) {
            return false;
        }

        const sessionKey = getFilterSessionKey({
            isVehicleFeatureCard: true,
            randomVehicleFeature: feature,
        });
        return !hasSeenFilter(sessionKey);
    });

    // Check Suggested filters
    const hasUnseenSuggestedFilter = suggestedFilters.some((filter) => {
        if (isFilterAlreadyActive(filter, activeFilterNames)) return false;
        const sessionKey = getFilterSessionKey({
            isSuggestedFilter: true,
            firstSuggestedFilter: filter,
        });
        return !hasSeenFilter(sessionKey);
    });

    // Return true if ALL categories have no unseen filters
    return !hasUnseenStrongAttribute
           && !hasUnseenTruckFilter
           && !hasUnseenSUVFilter
           && !hasUnseenVehicleFeature
           && !hasUnseenSuggestedFilter;
};

/**
 * Calculate vehicle style state (truck/SUV card logic)
 */
const calculateVehicleStyleState = (params) => {
    const {
        firstStrongAttribute,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
    } = params;

    const thirdRowFilterOptions = featureFilterOptions?.options?.find(
        (option) => option.value === '1306'
    );
    const thirdRowApplied = featureFilterValues.includes('1306');
    const cabSizeApplied = cabSizeValues.length > 0;

    // Truck card logic
    const shouldShowTruckCard = !firstStrongAttribute
                               && isTruckSelected
                               && !cabSizeApplied
                               && !_isEmpty(cabSizeOptions);

    const truckSessionKey = shouldShowTruckCard
        ? getFilterSessionKey({ isTruckCard: true }) : null;

    const isTruckCard = shouldShowTruckCard && !hasSeenFilter(truckSessionKey);

    // SUV card logic
    const shouldShowSUVCard = !firstStrongAttribute
                             && !isTruckCard
                             && isSUVSelected
                             && !thirdRowApplied
                             && !_isEmpty(thirdRowFilterOptions);

    const suvSessionKey = shouldShowSUVCard
        ? getFilterSessionKey({ isSUVCard: true }) : null;

    const isSUVCard = shouldShowSUVCard && !hasSeenFilter(suvSessionKey);

    return {
        isTruckCard,
        isSUVCard,
        thirdRowApplied,
        cabSizeApplied,
    };
};

/**
 * Calculate vehicle feature state
 */
const calculateVehicleFeatureState = (params) => {
    const {
        firstStrongAttribute,
        isTruckCard,
        isSUVCard,
        featureFilterOptions,
        featureFilterValues = [],
        activeFilterNames = [],
    } = params;

    const availableVehicleFeatures = getAvailableVehicleFeatures(featureFilterOptions);
    const hasVehicleFeatures = !_isEmpty(availableVehicleFeatures);
    const firstUnseenVehicleFeature = getFirstUnseenVehicleFeature(availableVehicleFeatures, featureFilterValues, activeFilterNames);
    const hasUnseenVehicleFeatures = Boolean(firstUnseenVehicleFeature);

    const shouldShowVehicleFeatureCard = !firstStrongAttribute
                                        && !isTruckCard
                                        && !isSUVCard
                                        && hasVehicleFeatures
                                        && hasUnseenVehicleFeatures;

    const isVehicleFeatureCard = shouldShowVehicleFeatureCard && Boolean(firstUnseenVehicleFeature);

    return {
        isVehicleFeatureCard,
        availableVehicleFeatures,
        hasVehicleFeatures,
        hasUnseenVehicleFeatures,
        firstUnseenVehicleFeature,
    };
};

/**
 * Calculate filter hierarchy based on priority rules and seen state
 */
export const getFilterHierarchy = (params) => {
    const {
        activeFilterValues = [],
        suggestedFilters = [],
        strongAttributeFilters = [],
        isTruckSelected = false,
        isSUVSelected = false,
        featureFilterOptions = null,
        featureFilterValues = [],
        cabSizeOptions = [],
        cabSizeValues = [],
    } = params;

    // Helper to get active filter names
    const activeFilterNames = activeFilterValues.map((filter) => filter.filterName);

    // Check if all filters in hierarchy have been seen and reset if needed
    const allFiltersSeenParams = {
        strongAttributeFilters,
        suggestedFilters,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
        activeFilterNames,
    };

    if (areAllFiltersInHierarchySeen(allFiltersSeenParams)) {
        clearFilterSeenData();
    }

    // Find first inactive and unseen filters
    const firstStrongAttribute = findFirstInactiveUnseenFilter(
        strongAttributeFilters,
        activeFilterNames,
        FILTER_TYPES.STRONG_ATTRIBUTE
    );

    const firstSuggestedFilter = findFirstInactiveUnseenFilter(
        suggestedFilters,
        activeFilterNames,
        FILTER_TYPES.SUGGESTED
    );

    // Priority 1: Strong Attributes
    const isStrongAttribute = Boolean(firstStrongAttribute);

    // Priority 2: Truck/SUV Bodystyle substyle
    const vehicleStyleState = calculateVehicleStyleState({
        firstStrongAttribute,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
    });

    // Priority 3: Vehicle Features
    const vehicleFeatureState = calculateVehicleFeatureState({
        firstStrongAttribute,
        isTruckCard: vehicleStyleState.isTruckCard,
        isSUVCard: vehicleStyleState.isSUVCard,
        featureFilterOptions,
        featureFilterValues,
        activeFilterNames,
    });

    // Priority 4: Suggested Filters
    const isSuggestedFilter = !isStrongAttribute
                             && !vehicleStyleState.isTruckCard
                             && !vehicleStyleState.isSUVCard
                             && !vehicleFeatureState.isVehicleFeatureCard
                             && Boolean(firstSuggestedFilter);

    return {
        // Priority flags
        isStrongAttribute,
        isTruckCard: vehicleStyleState.isTruckCard,
        isSUVCard: vehicleStyleState.isSUVCard,
        isVehicleFeatureCard: vehicleFeatureState.isVehicleFeatureCard,
        isSuggestedFilter,

        // Filter data
        firstStrongAttribute,
        firstSuggestedFilter,
        randomVehicleFeature: vehicleFeatureState.firstUnseenVehicleFeature,
        availableVehicleFeatures: vehicleFeatureState.availableVehicleFeatures,

        // Helper values
        activeFilterNames,
        hasVehicleFeatures: vehicleFeatureState.hasVehicleFeatures,
        hasUnseenVehicleFeatures: vehicleFeatureState.hasUnseenVehicleFeatures,
        thirdRowApplied: vehicleStyleState.thirdRowApplied,
        cabSizeApplied: vehicleStyleState.cabSizeApplied,
    };
};

export default getFilterHierarchy;
